/**
 * Portal Kit: regras puras do painel (estado do site, idade, versões, rótulos).
 *
 * Sem React e sem rede, para o teste poder fixar o relógio. O estado usa as três
 * cores semânticas do MCUI (ok, atenção, crítico) e SEMPRE vem com o motivo em
 * texto: cor sozinha não diz o que fazer.
 */
import {
  COLUNAS,
  type ColunaId,
  type ItemConferencia,
  type PortalSiteResumo,
} from '@/types/portal';

/** O plugin sincroniza o manifesto a cada 5 minutos (ADR 0024, D-e). */
export const CICLO_SYNC_S = 300;
/** Até dois ciclos sem ler ainda é saudável: a leitura cai entre dois relógios. */
export const LIMITE_OK_S = 2 * CICLO_SYNC_S;
/** Depois de seis ciclos (30 min) o plugin parou de sincronizar. */
export const LIMITE_CRITICO_S = 6 * CICLO_SYNC_S;

export type EstadoSemantico = 'ok' | 'warn' | 'crit';

export interface EstadoDoSite {
  estado: EstadoSemantico;
  /** Frase pronta para a tela: por que está nesta cor e, quando cabe, o que fazer. */
  motivo: string;
  /** Segundos desde a última leitura do manifesto; nulo se nunca leu. */
  idade_s: number | null;
}

const GRAVIDADE: Record<EstadoSemantico, number> = { ok: 0, warn: 1, crit: 2 };

/** "há 45 s", "há 3 min", "há 2 h", "há 4 d" ou "nunca". */
export function descreverIdade(segundos: number | null | undefined): string {
  if (segundos === null || segundos === undefined || Number.isNaN(segundos)) return 'nunca';
  const s = Math.max(0, Math.round(segundos));
  if (s < 60) return `há ${s} s`;
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86_400) return `há ${Math.floor(s / 3600)} h`;
  return `há ${Math.floor(s / 86_400)} d`;
}

/** Compara duas versões `maior.menor.patch[-pre]`. Negativo se a < b. Pré-lançamento vale menos. */
export function compararSemver(a: string, b: string): number {
  const separa = (v: string) => {
    const [nucleo, pre = ''] = v.trim().split('-', 2);
    const partes = nucleo.split('.').map((p) => Number.parseInt(p, 10) || 0);
    while (partes.length < 3) partes.push(0);
    return { partes, pre };
  };
  const x = separa(a);
  const y = separa(b);
  for (let i = 0; i < 3; i += 1) {
    if (x.partes[i] !== y.partes[i]) return x.partes[i] - y.partes[i];
  }
  if (x.pre === y.pre) return 0;
  if (x.pre === '') return 1;
  if (y.pre === '') return -1;
  return x.pre < y.pre ? -1 : 1;
}

/** Segundos entre uma data ISO e `agora` (ms). Nulo se a data não existe ou não lê. */
export function segundosDesde(iso: string | null | undefined, agora: number): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((agora - t) / 1000));
}

/**
 * Estado de um site no painel. Junta o que está errado, escolhe o mais grave e
 * devolve a frase dele. `agora` é injetado para o teste fixar o relógio.
 */
export function estadoDoSite(site: PortalSiteResumo, agora: number = Date.now()): EstadoDoSite {
  const inst = site.instalacao;
  const idade = segundosDesde(inst?.ultima_leitura_em, agora);
  const problemas: { estado: EstadoSemantico; motivo: string }[] = [];

  if (!site.token) {
    problemas.push({
      estado: 'warn',
      motivo: 'Sem token. Gere um na aba Conexão para o plugin ler o manifesto.',
    });
  } else if (!inst) {
    problemas.push({
      estado: 'warn',
      motivo: 'Token pronto, mas o plugin ainda não leu o manifesto.',
    });
  }

  if (inst) {
    if (idade !== null && idade > LIMITE_CRITICO_S) {
      problemas.push({
        estado: 'crit',
        motivo: `Último contato do plugin ${descreverIdade(idade)}: parou de sincronizar.`,
      });
    } else if (idade !== null && idade > LIMITE_OK_S) {
      problemas.push({
        estado: 'warn',
        motivo: `Último contato do plugin ${descreverIdade(idade)}: sincronização atrasada.`,
      });
    }
    if (compararSemver(inst.versao, site.plugin.versao_minima) < 0) {
      problemas.push({
        estado: 'crit',
        motivo: `Plugin ${inst.versao} abaixo do mínimo ${site.plugin.versao_minima}: atualize.`,
      });
    } else if (compararSemver(inst.versao, site.plugin.versao_atual) < 0) {
      problemas.push({
        estado: 'warn',
        motivo: `Há versão nova do plugin (${site.plugin.versao_atual}); este site usa ${inst.versao}.`,
      });
    }
    if (inst.conflitos.length > 0) {
      const n = inst.conflitos.length;
      problemas.push({
        estado: 'warn',
        motivo: `${n} ${n === 1 ? 'rota' : 'rotas'} em conflito com conteúdo que já existia no WordPress.`,
      });
    }
  }

  if (problemas.length === 0) {
    return {
      estado: 'ok',
      motivo: `Manifesto lido ${descreverIdade(idade)}.`,
      idade_s: idade,
    };
  }
  const pior = problemas.reduce((a, b) => (GRAVIDADE[b.estado] > GRAVIDADE[a.estado] ? b : a));
  return { estado: pior.estado, motivo: pior.motivo, idade_s: idade };
}

/**
 * Estado de uma linha da Conferência. Sem hora do dado é crítico (a fonte não
 * entregou); atraso acima do limite do item é atenção; o resto está em dia.
 */
export function estadoDoItem(item: ItemConferencia): EstadoSemantico {
  if (!item.as_of) return 'crit';
  if (item.atraso_min !== null && item.atraso_min > item.atraso_alerta_min) return 'warn';
  return 'ok';
}

export function descreverAtrasoMin(min: number | null | undefined): string {
  if (min === null || min === undefined) return 'sem medida';
  if (min < 1) return 'em dia';
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  const r = Math.round(min % 60);
  return r === 0 ? `${h} h` : `${h} h ${r} min`;
}

export interface InfoColuna {
  rotulo: string;
  resumo: string;
}

export const INFO_COLUNAS: Record<ColunaId, InfoColuna> = {
  tempo: {
    rotulo: 'Previsão do tempo',
    resumo: 'Cidades com página própria, previsão e cartões de condição agora.',
  },
  mapa: {
    rotulo: 'Mapa e avisos',
    resumo: 'Mapa do Brasil com os avisos oficiais do INMET sobre as cidades do tempo.',
  },
  agro: {
    rotulo: 'Agro',
    resumo: 'Praças com selo de geada e janela de pulverização, calculados pelos seus limiares.',
  },
  mercado: {
    rotulo: 'Mercado',
    resumo: 'Ticker, painel das matérias diárias e página de cotações.',
  },
  horoscopo: {
    rotulo: 'Horóscopo',
    resumo: 'Signos com página e o texto do dia.',
  },
  loterias: {
    rotulo: 'Loterias',
    resumo: 'Modalidades reconhecidas nos resultados e quadro de outros resultados.',
  },
  futebol: {
    rotulo: 'Futebol',
    resumo: 'Tabelas das séries e as páginas de rodada, jogo, clube e atleta.',
  },
  servicos: {
    rotulo: 'Serviços ao vivo',
    resumo: 'Faixa de serviços do cabeçalho, cartões da home e barra de mercados.',
  },
  fonte: {
    rotulo: 'Fonte das matérias',
    resumo: 'Exige o link da fonte antes de a matéria ir ao ar.',
  },
};

export function rotuloDaColuna(c: ColunaId): string {
  return INFO_COLUNAS[c].rotulo;
}

export function ehColunaConhecida(v: string): v is ColunaId {
  return (COLUNAS as readonly string[]).includes(v);
}

/** Endereço que o plugin cola no campo "URL da API de dados". */
export function urlPublicaDaApiDeDados(apiBase: string, origem: string): string {
  const base = apiBase.startsWith('http') ? apiBase : `${origem}${apiBase}`;
  return `${base.replace(/\/+$/, '')}/dados`;
}
